import { useMutation, useQuery } from "@tanstack/react-query";

import { queryClient } from "@/config/react-query";

import { usePatientPortal } from "@/contexts/patient-portal-provider";

import {
  PatientPortalAppointmentsResponse,
  PatientPortalFormsResponse,
} from "@/types/patient-portal";
import { WearableConnection } from "@/types/wearable";

import {
  PatientPortalCustomMeasurementsResponse,
  PatientPortalProfileUpdate,
  cancelPatientAppointment,
  confirmPatientWearableConnection,
  createPatientWearableConnectSession,
  disconnectPatientWearable,
  getPatientAppointments,
  getPatientCustomMeasurements,
  getPatientForms,
  getPatientMe,
  getPatientPortalForm,
  getPatientWearables,
  updatePatientMe,
} from "@/api/patient/portal";

export const patientPortalQueryKeys = {
  all: ["patient-portal"] as const,
  me: (userScope: string) =>
    [...patientPortalQueryKeys.all, "me", userScope] as const,
  appointments: (userScope: string) =>
    [...patientPortalQueryKeys.all, "appointments", userScope] as const,
  forms: (userScope: string) =>
    [...patientPortalQueryKeys.all, "forms", userScope] as const,
  formDetail: (
    formPathName: string,
    answeredFormId: string,
    patientId: string,
  ) =>
    [
      ...patientPortalQueryKeys.all,
      "form-detail",
      formPathName,
      answeredFormId,
      patientId,
    ] as const,
  customMeasurements: (userScope: string) =>
    [...patientPortalQueryKeys.all, "custom-measurements", userScope] as const,
  wearables: (userScope: string) =>
    [...patientPortalQueryKeys.all, "wearables", userScope] as const,
};

export function usePatientMe(userScope: string, enabled = true) {
  return useQuery({
    queryKey: patientPortalQueryKeys.me(userScope),
    queryFn: () => getPatientMe(),
    enabled: enabled && !!userScope,
  });
}

/**
 * How often the connections list re-reads while a connect attempt is in flight.
 *
 * The aggregator confirms to the patient by redirecting their browser, but the
 * connection row is written server-side, on a path that browser never touches —
 * so the moment the patient is looking at this page is exactly the moment the
 * row may not exist yet. Polling closes that window; without it the patient sees
 * "no devices connected" right after connecting one.
 */
const WEARABLES_POST_CONNECT_POLL_MS = 3_000;

/**
 * Hoisted out of the hook, both of them, and for two compounding reasons.
 * React Query memoizes `select` on the data AND the function's identity, so an
 * inline arrow re-runs it every render; and `data ?? []` mints a fresh array
 * each time it runs. Together they gave `connections` a new reference on every
 * render, which fired every effect keyed on it — harmless only while those
 * effect bodies were guarded.
 */
const EMPTY_WEARABLE_CONNECTIONS: WearableConnection[] = [];

const selectWearableConnections = (
  data: WearableConnection[] | null,
): WearableConnection[] => data ?? EMPTY_WEARABLE_CONNECTIONS;

export function usePatientAppointments(enabled = true) {
  const { patient } = usePatientPortal();
  const userScope = patient?.id ?? "";
  return useQuery({
    queryKey: patientPortalQueryKeys.appointments(userScope),
    queryFn: getPatientAppointments,
    enabled: enabled && !!userScope,
    select: (data): PatientPortalAppointmentsResponse =>
      data ?? { upcoming: [], past: [], cancelled: [] },
  });
}

export function usePatientCustomMeasurements(enabled = true) {
  const { patient } = usePatientPortal();
  const userScope = patient?.id ?? "";
  return useQuery({
    queryKey: patientPortalQueryKeys.customMeasurements(userScope),
    queryFn: getPatientCustomMeasurements,
    enabled: enabled && !!userScope,
    select: (data): PatientPortalCustomMeasurementsResponse =>
      data ?? { groups: [], fields: [], values: [] },
  });
}

/**
 * The patient's connected devices.
 *
 * `awaitingConnection` turns on the short poll described above; callers switch
 * it off as soon as the expected row appears.
 */
export function usePatientWearables(
  enabled = true,
  awaitingConnection = false,
) {
  const { patient } = usePatientPortal();
  const userScope = patient?.id ?? "";
  return useQuery({
    queryKey: patientPortalQueryKeys.wearables(userScope),
    queryFn: getPatientWearables,
    enabled: enabled && !!userScope,
    refetchInterval: awaitingConnection
      ? WEARABLES_POST_CONNECT_POLL_MS
      : false,
    select: selectWearableConnections,
  });
}

/**
 * Records the device the patient just authorised, instead of waiting for a sweep.
 *
 * The aggregator's own report of the authorisation goes to the export bucket and
 * is read once a day; this is the fast path for the patient who lands back on the
 * page. The `terraUserId` reached the browser in a redirect and is verified
 * against the aggregator by the backend before anything is written.
 */
export function useConfirmWearableConnectionMutation() {
  return useMutation({
    mutationFn: (terraUserId: string) =>
      confirmPatientWearableConnection(terraUserId),
  });
}

export function useCreateWearableConnectSessionMutation() {
  return useMutation({
    mutationFn: createPatientWearableConnectSession,
  });
}

export function useDisconnectWearableMutation() {
  const { patient } = usePatientPortal();
  const userScope = patient?.id ?? "";
  return useMutation({
    mutationFn: (connectionId: string) =>
      disconnectPatientWearable(connectionId),
    onSuccess: (updated) => {
      if (!updated) return;
      // Written into the cache rather than invalidated: the response is the
      // updated row, so re-reading the list would cost a round trip to learn
      // what we already hold, and would flicker the card back to Connected in
      // between.
      queryClient.setQueryData<WearableConnection[]>(
        patientPortalQueryKeys.wearables(userScope),
        (existing) =>
          existing?.map((connection) =>
            connection.id === updated.id ? updated : connection,
          ),
      );
    },
  });
}

export function usePatientForms(enabled = true) {
  const { patient } = usePatientPortal();
  const userScope = patient?.id ?? "";
  return useQuery({
    queryKey: patientPortalQueryKeys.forms(userScope),
    queryFn: getPatientForms,
    enabled: enabled && !!userScope,
    select: (data): PatientPortalFormsResponse =>
      data ?? { pending: [], completed: [] },
  });
}

export function useFormDetail(
  formPathName: string | undefined,
  answeredFormId?: string,
) {
  const { patient } = usePatientPortal();
  const patientId = patient?.id ?? "";
  return useQuery({
    queryKey: patientPortalQueryKeys.formDetail(
      formPathName ?? "",
      answeredFormId ?? "",
      patientId,
    ),
    queryFn: () => getPatientPortalForm(formPathName!, answeredFormId),
    enabled: !!formPathName && !!patientId,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
  });
}

export function useUpdatePatientMeMutation() {
  return useMutation({
    mutationFn: (update: PatientPortalProfileUpdate) => updatePatientMe(update),
  });
}

export function useCancelAppointmentMutation() {
  const { patient } = usePatientPortal();
  const userScope = patient?.id ?? "";
  return useMutation({
    mutationFn: ({
      appointmentId,
      reason,
    }: {
      appointmentId: string;
      reason?: string;
    }) => cancelPatientAppointment(appointmentId, reason),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: patientPortalQueryKeys.appointments(userScope),
      });
      queryClient.invalidateQueries({
        queryKey: patientPortalQueryKeys.forms(userScope),
      });
    },
  });
}
